import { Fragment, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserRound, FileText, Calendar, CreditCard, LineChart,
  ChevronRight, ChevronDown, ArrowRight,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useBranding } from "../contexts/BrandingContext";
import { gaTrack, metaTrack, signupAudience } from "@/lib/analytics";
import { trackEvent } from "@/lib/tracker";
import SEO from "../components/SEO";
import useBottomBarClearance from "../hooks/useBottomBarClearance";
import LandingSocialProof from "../components/LandingSocialProof";
import SocialAuthButtons from "../components/SocialAuthButtons";
import { resolvePostLoginPath } from "../utils/postAuth";

/* ================================================================== */
/*  TALENT landing. Mirror of the business page (one audience, one     */
/*  message): hook → problem → one-system statement → proven process → */
/*  sign-up. The core problem it solves: the overhead of being         */
/*  independent. Sift runs the back office so you can do the work.      */
/* ================================================================== */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Geist+Mono:wght@400;500&family=Caveat:wght@500;600;700&display=swap');
.tl { background:var(--bg); color:var(--ink); font-family:var(--font-sans); }
.tl__wrap { max-width:1080px; margin:0 auto; padding:0 24px; }
.tl-mono { font-family:var(--font-mono); text-transform:uppercase; }
.tl-serif { font-family:var(--font-sans); letter-spacing:-.02em; }
.tl-em { font-style:italic; }
.tl-cta { display:inline-block; background:var(--brand); color:#fff; padding:15px 34px; border-radius:var(--r-pill); font-weight:600; font-size:1rem; box-shadow:var(--shadow-md); transition:background .2s; }
.tl-cta:hover { background:var(--brand-hover); }
.tl-hero { position:relative; overflow-x:clip; } /* the glow bleeds 8px past the sides; at 320px that scrolled the page (a11y session 9) */
.tl-hero-glow { position:absolute; inset:-30px -8px 0; z-index:-1; pointer-events:none;
  background:radial-gradient(40% 55% at 25% 25%, var(--brand-tint), transparent 72%),
            radial-gradient(36% 48% at 82% 20%, var(--gold-soft), transparent 72%);
  opacity:.7; }

/* Problem section: text-only since the name-tags visual was removed. */
.tl-solution { max-width:760px; margin:30px auto 0; background:var(--ink); color:var(--on-dark); border-radius:var(--r-2xl); padding:26px 28px; text-align:center; font-weight:600; font-size:clamp(1.1875rem,2.4vw,1.5rem); line-height:1.3; }

/* Proven process as a flow map: connected nodes A → B → C → sign up.
   Horizontal on desktop, a vertical flow (arrows rotate down) on mobile. */
.tl-flow { display:flex; align-items:flex-start; justify-content:center; max-width:1040px; margin:34px auto 0; }
.tl-node { flex:1 1 0; min-width:0; display:flex; flex-direction:column; align-items:center; text-align:center; padding:0 6px; text-decoration:none; }
.tl-node__dot { position:relative; width:56px; height:56px; border-radius:50%; background:var(--surface); border:2px solid var(--brand); display:grid; place-items:center; box-shadow:var(--shadow-sm); transition:transform .2s; }
.tl-node--end .tl-node__dot { background:var(--brand); border-color:var(--brand); }
.tl-node--end:hover .tl-node__dot { transform:scale(1.06); }
.tl-node__num { position:absolute; top:-7px; right:-7px; width:22px; height:22px; border-radius:50%; background:var(--brand); color:#fff; font-size:0.6875rem; font-weight:700; display:grid; place-items:center; border:2px solid var(--bg); }
.tl-node__title { font-weight:600; font-size:0.90625rem; color:var(--ink); margin:12px 0 3px; }
.tl-node__cap { font-size:0.78125rem; line-height:1.4; color:var(--text-secondary); }
.tl-arrow { flex:0 0 auto; display:flex; align-items:flex-start; padding-top:19px; color:var(--brand); }
@media (max-width:820px){
  .tl-flow { flex-direction:column; align-items:stretch; max-width:360px; }
  .tl-node { flex-direction:row; text-align:left; gap:14px; align-items:center; padding:2px 0; }
  .tl-node__title { margin:0 0 2px; }
  .tl-arrow { justify-content:flex-start; padding:6px 0 6px 22px; transform:rotate(90deg); transform-origin:left center; }
}

/* Sticky closing CTA, pinned to the bottom of the viewport so the ask stays
   in reach the whole scroll. Sits above the page footer; page gets bottom
   padding so nothing hides behind it. */
.tl-stickybar { position:fixed; left:0; right:0; bottom:0; z-index:40;
  background:rgba(250,248,242,.9); backdrop-filter:saturate(180%) blur(12px); -webkit-backdrop-filter:saturate(180%) blur(12px);
  border-top:1px solid var(--border); padding:12px 24px calc(12px + env(safe-area-inset-bottom)); box-shadow:0 -8px 30px -18px rgba(27,24,19,.4); }
.tl-stickybar__wrap { max-width:1080px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.tl-stickybar__msg { font-weight:600; font-size:0.9375rem; }
.tl-stickybar__sub { font-weight:400; font-size:0.8125rem; color:var(--text-secondary); }
.tl-stickybar__cta { flex:0 0 auto; display:inline-flex; align-items:center; gap:8px; background:var(--brand); color:#fff; padding:13px 30px; border-radius:var(--r-pill); font-weight:700; font-size:0.96875rem; box-shadow:var(--shadow-md); transition:background .2s; white-space:nowrap; }
.tl-stickybar__cta:hover { background:var(--brand-hover); }
@media (max-width:560px){
  .tl-stickybar__msg { font-size:0.875rem; }
  .tl-stickybar__sub { display:none; }
  .tl-stickybar__cta { padding:12px 22px; font-size:0.90625rem; }
}
`;

// Proven process as a flow map. Short captions keep each node compact so the
// A → B → C flow reads at a glance. [Icon, title, caption].
const STEPS = [
  [UserRound, "Get found", "Profile and availability"],
  [FileText, "Agree on terms", "Contracts in one place"],
  [Calendar, "Schedule", "Pushed to your calendar"],
  [CreditCard, "Get paid", "Timecards and payment together"],
  [LineChart, "Know your numbers", "Analytics and exports"],
];

/* The five-step flow, extracted so it can sit directly under the hero. The
   #how-it-works id travels with it — existing links still resolve. */
function SiftMethod() {
  return (
    <section id="how-it-works" className="tl__wrap" style={{ padding: "44px 24px 8px", textAlign: "center", scrollMarginTop: 80 }}>
      <div className="tl-mono" style={{ fontSize: "0.65625rem", letterSpacing: ".28em", color: "var(--brand)" }}>The Sift Method</div>
      <h2 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.5rem,2.8vw,2rem)", margin: "10px auto 0", maxWidth: 640, lineHeight: 1.14 }}>
        Six costly tools become <span className="tl-em">one free one.</span>
      </h2>

      <div className="tl-flow">
        {STEPS.map(([Icon, title, cap], i) => (
          <Fragment key={title}>
            <div className="tl-node">
              <span className="tl-node__dot" aria-hidden="true">
                <Icon size={24} strokeWidth={1.7} color="var(--brand)" />
                <span className="tl-node__num">{i + 1}</span>
              </span>
              <div>
                <div className="tl-node__title">{title}</div>
                <div className="tl-node__cap">{cap}</div>
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className="tl-arrow" aria-hidden="true">
                <ChevronRight size={22} strokeWidth={2.4} />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div style={{ textAlign: "center", marginTop: 30 }}>
        <Link to="/get-started?a=talent" className="tl-cta" data-analytics="cta_get_started:talent_post_method" onClick={() => gaTrack("cta_get_started", { location: "talent_post_method", account_type: "individual" })}>
          Post your availability
        </Link>
      </div>

      {/* The Sift Score note that used to sit here now has its own section
          below, so the flow ends on the ask instead of a footnote. */}
    </section>
  );
}

export default function TalentLanding({ adLP = false }) {
  const branding = useBranding();
  const stickyRef = useRef(null);
  useBottomBarClearance(stickyRef);
  const navigate = useNavigate();

  const onSocialAuthed = async (data, provider) => {
    // A brand-new SSO user is a real signup. account_created / sign_up /
    // CompleteRegistration come from SocialAuthButtons, which is the only place
    // that knows which provider was used; firing them here as well double-counted
    // every social signup off this page. What is left is the part this page owns.
    if (data?.is_new_user) {
      trackEvent("verification_completed", { method: "oauth", provider, account_type: "individual" });
      metaTrack("StartTrial", signupAudience("individual"));
    }
    toast.success("You're in: welcome to Sift!");
    let dest = "/home";
    // `data.user` is already in hand from the OAuth response; passing it skips
    // the resolver's /me/ round trip.
    try { dest = await resolvePostLoginPath(branding, data?.user); } catch { /* fall back */ }
    navigate(dest);
  };

  return (
    <div className="tl">
      <SEO
        title="A Trusted Services Marketplace"
        description="Sift Careers gives independent professionals the infrastructure to run themselves like a business: get found, sign contracts, schedule, get paid, and track your numbers in one free tool."
        path="/for-talent"
        keywords={["be your own boss", "independent professional platform", "freelance back office", "contractor invoicing and contracts", "credential tracking", "get hired directly", "self employed tools"]}
        noIndex={adLP}
      />
      <style>{CSS}</style>

      {/* ============================= HERO ============================= */}
      <section className="tl__wrap tl-hero" style={{ paddingTop: 40, paddingBottom: 0, textAlign: "center" }}>
        <div className="tl-hero-glow" aria-hidden="true" />
        <div className="tl-mono" style={{ fontSize: "0.65625rem", letterSpacing: ".2em", color: "var(--brand)" }}>
          Consultants &amp; contractors
        </div>
        <h1 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(2rem,4vw,3rem)", lineHeight: 1.05, margin: "14px auto 0", maxWidth: 720 }}>
          Your skills. Your rates. <span className="tl-em" style={{ fontWeight: 600 }}>Your clients.</span>
        </h1>
        <p style={{ fontSize: "clamp(1.0625rem,2.2vw,1.3125rem)", lineHeight: 1.4, color: "var(--ink)", fontWeight: 600, maxWidth: 640, margin: "16px auto 0" }}>
          Sift connects you directly with businesses that need what you do. No bidding wars, no buying credits to apply, and{" "}
          <span style={{ color: "var(--brand)" }}>no cut of your rate.</span>
        </p>
        {/* The hero now carries the primary ask. It names the action (post),
            not the account, and ?a=talent skips the audience chooser so the
            click lands in the individual signup and then straight in the
            availability composer. */}
        <div style={{ marginTop: 24 }}>
          <Link
            to="/get-started?a=talent"
            className="tl-cta"
            data-analytics="cta_get_started:talent_hero"
            onClick={() => gaTrack("cta_get_started", { location: "talent_hero", account_type: "individual" })}
          >
            Post your availability
          </Link>
          <div style={{ marginTop: 12, fontSize: "0.84375rem", color: "var(--text-tertiary)" }}>
            Free, always. Tell us the work you do and we'll write the post for you.
          </div>
          <div style={{ marginTop: 14 }}>
            <a href="#how-it-works" style={{ fontSize: "0.90625rem", color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              Or see how it works <ChevronDown size={15} strokeWidth={2} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* ============== THE SIFT METHOD (moved above the problem) ==============
          Same reordering as the business page: state what the product does in
          one glance, and keep the ask in the first screenful, instead of
          making a visitor scroll past the name-tags graphic to find out. */}
      <SiftMethod />

      {/* ============================= PROBLEM ============================= */}
      <section id="the-problem" className="tl__wrap" style={{ padding: "30px 24px 8px", textAlign: "center", scrollMarginTop: 80 }}>
        <div className="tl-mono" style={{ fontSize: "0.65625rem", letterSpacing: ".28em", color: "var(--brand)" }}>The problem</div>
        <h2 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.625rem,3vw,2.125rem)", margin: "10px auto 0", maxWidth: 660, lineHeight: 1.12 }}>
          Gig platforms weren't built for professionals.
        </h2>
        <p style={{ fontSize: "1.03125rem", lineHeight: 1.55, color: "var(--text-secondary)", maxWidth: 620, margin: "16px auto 0" }}>
          You've done the freelance-marketplace thing. Pay to submit proposals, undercut your own rate to win the work, then hand over a cut of every dollar for as long as the client lasts.
        </p>
        <p style={{ fontSize: "1.03125rem", lineHeight: 1.55, color: "var(--text-secondary)", maxWidth: 620, margin: "12px auto 0" }}>
          That model works for the platform. It doesn't work for you.
        </p>

        {/* The scattered "Hello, I'm the ___" name tags used to sit here.
            Removed outright (VAL's call), same reasoning as the business page:
            the Sift Method flow above carries the visual weight now. The
            composition is preserved in the design doc (Name Tags.dc.html). */}
        <div className="tl-solution">
          Sift runs the back office so you can do the actual work.
        </div>
      </section>

      {/* ===================== SIFT SCORE ===================== */}
      <section className="tl__wrap" style={{ padding: "44px 24px 8px", textAlign: "center" }}>
        <h2 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.5rem,2.8vw,2rem)", margin: 0, maxWidth: 640, marginInline: "auto", lineHeight: 1.14 }}>
          Your reputation, portable and provable.
        </h2>
        <p style={{ fontSize: "1.03125rem", lineHeight: 1.55, color: "var(--text-secondary)", maxWidth: 620, margin: "16px auto 0" }}>
          Your <strong style={{ color: "var(--ink)" }}>Sift Score</strong> grows with every verified credential, completed engagement, and client rating. It isn't an algorithm ranking you against 10,000 bidders. It's proof of who you are, working for you on every match.
        </p>
      </section>

      {/* ============== THE RELATIONSHIP IS YOURS ==============
          Mirror of the business page's no-conversion-fee section, from the
          worker's side. Same policy, and it's a reason to trust the platform
          with the client relationship. */}
      <section className="tl__wrap" style={{ padding: "44px 24px 8px", textAlign: "center" }}>
        <h2 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.5rem,2.8vw,2rem)", margin: 0, maxWidth: 660, marginInline: "auto", lineHeight: 1.14 }}>
          If a client wants to hire you full-time, <span className="tl-em">congratulations.</span>
        </h2>
        <p style={{ fontSize: "1.03125rem", lineHeight: 1.55, color: "var(--text-secondary)", maxWidth: 620, margin: "16px auto 0" }}>
          No conversion fees, no non-circumvention traps. Great contract work sometimes becomes a great job. We won't stand in the way of your career.
        </p>
      </section>

      {/* ============= SOCIAL PROOF — real profiles + posted availabilities ============= */}
      <div style={{ marginTop: 40 }}>
        <LandingSocialProof />
      </div>

      {/* ============================= CTA BAND ============================= */}
      <section className="tl__wrap" style={{ padding: "40px 24px 60px" }}>
        <div style={{ position: "relative", overflow: "hidden", background: "var(--ink)", color: "var(--on-dark)", borderRadius: "var(--r-2xl)", padding: "48px 32px", textAlign: "center" }}>
          <div aria-hidden="true" style={{ position: "absolute", top: "-40%", left: "50%", transform: "translateX(-50%)", width: 520, height: 520, background: "radial-gradient(50% 50% at 50% 50%, rgba(224,201,136,.18), rgba(224,201,136,0) 70%)", pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <h2 className="tl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.625rem,3vw,2.25rem)", margin: 0, lineHeight: 1.08 }}>
              Your whole business, in one profile.
            </h2>
            <p style={{ color: "var(--on-dark-muted)", maxWidth: 460, margin: "14px auto 24px", fontSize: "1rem" }}>
              Free to use. Set up in minutes.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
              <Link to="/get-started?a=talent" data-analytics="cta_start_free:talent_cta_band" onClick={() => gaTrack("cta_get_started", { location: "talent_cta_band", account_type: "individual" })} style={{ background: "var(--brand)", color: "#fff", padding: "14px 32px", borderRadius: "var(--r-pill)", fontWeight: 600, whiteSpace: "nowrap" }}>
                Start free
              </Link>
            </div>

            <div style={{ maxWidth: 340, margin: "22px auto 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.18)" }} />
                <span className="tl-mono" style={{ fontSize: "0.625rem", letterSpacing: ".16em", color: "var(--on-dark-muted)" }}>or sign up instantly</span>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.18)" }} />
              </div>
              <SocialAuthButtons verb="Sign up" onAuthed={onSocialAuthed} accountType="individual" />
            </div>
          </div>
        </div>
      </section>

      {/* Spacer so the closing content clears the fixed sticky bar. */}
      <div aria-hidden="true" style={{ height: 84 }} />

      {/* ===================== STICKY CTA FOOTER ===================== */}
      <div ref={stickyRef} className="tl-stickybar" role="complementary" aria-label="Get started with Sift">
        <div className="tl-stickybar__wrap">
          <div>
            <div className="tl-stickybar__msg">Ready to run your whole business in one place?</div>
            <div className="tl-stickybar__sub">Free to use. Set up in minutes.</div>
          </div>
          <Link
            to="/get-started?a=talent"
            className="tl-stickybar__cta"
            onClick={() => gaTrack("cta_get_started", { location: "talent_sticky_bar", account_type: "individual" })}
          >
            Get started <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}

